import { useEffect, useMemo, useState } from "react";
import {
  UserGroupIcon,
  EyeIcon,
  DocumentTextIcon,
  FilmIcon,
  UserCircleIcon
} from "@heroicons/react/24/outline";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { auth } from "../firebase";
import { baseColor } from "..";
import { Company, initialCompany } from "../model/Companies";
import companiesQuery from "../firestore/Companies.query";

const navigation = [
  { name: "団体管理", href: "/companies", icon: UserGroupIcon, current: false, },
  { name: "動画コンテンツ管理", href: "/videoContents", icon: FilmIcon, current: false, },
  { name: "利用状況", href: "/usageSituation", icon: EyeIcon, current: false, },
  { name: "利用規約・個人情報", href: "/termsOfUseManagement", icon: DocumentTextIcon, current: false, },
  { name: "管理者アカウント作成", href: "/register", icon: UserCircleIcon, current: false, },
];

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(" ");
}

const style = {
  color: {
    blue: `bg-blue-50 border-blue-600 text-blue-600`,
    green: `bg-green-50 border-green-600 text-green-600`,
    red: `bg-red-50 border-red-600 text-red-600`,
    yellow: `bg-yellow-50 border-yellow-600 text-yellow-600`,
    indigo: `bg-indigo-50 border-indigo-600 text-indigo-600`,
  },
};

export const Sidebar = () => {
  // URLパラメータからtenantId取得
  const { tenantId } = useParams<{ tenantId: string }>();

  const [company, setCompany] = useState<Company>(initialCompany);
  const navigate = useNavigate();
  const location = useLocation();
  const auhtLogout = () => {
    auth.signOut().then(() => {
      console.log("Sign-out successful.");
      sessionStorage.clear();
      navigate("/login");
    })
    .catch((err) => {
      console.error(err.message);
    });
  };

  const selectStyles = (to: string) => {
    const pathname = location.pathname;
    if (to === "/") {
      return pathname === to
        ? style.color[baseColor]
        : "border-transparent text-gray-600 hover:bg-gray-50 hover:text-gray-900";
    } else if (pathname.match(to)) {
      return style.color[baseColor];
    } else {
      return "border-transparent text-gray-600 hover:bg-gray-50 hover:text-gray-900";
    }
  };

  useMemo(() => {
    const f = async () => {
      if(tenantId !== undefined){
        // 団体情報
        const company = await companiesQuery.getCompany(tenantId);
        setCompany(company);
      } else {
        setCompany(initialCompany);
      }
    };
    f();
  }, [tenantId]);

  useEffect(() => {
    if(location.pathname === "/"){
      navigate("/companies");
    }
  },[location.pathname, navigate]);

  return (
    <>
      <div className="flex">
        {/* Sidebar component, swap this element with another sidebar if you like */}
        <div className="sticky flex flex-grow flex-col inset-y-0 overflow-y-auto border-r border-gray-200 bg-white pt-5 shadow w-56 h-[100vh] flex-shrink-0">
          <div className="text-xl flex flex-shrink-0 items-center px-4 font-bold tracking-tight text-gray-900">
            イベントサポート<br/>
            運営管理
          </div>
          <div className="mt-5 flex flex-grow flex-col">
            <nav className="flex-1 space-y-1 px-1 pb-4">
              {navigation.map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  className={classNames(
                    selectStyles(item.href),
                    "group flex items-center px-2 py-2 text-sm font-medium border-l-4"
                  )}
                >
                  <item.icon
                    className={classNames(
                      item.current
                        ? "text-gray-500"
                        : "text-gray-400 group-hover:text-gray-500",
                      "mr-2 flex-shrink-0 h-5 w-5"
                    )}
                    aria-hidden="true"
                  />
                  {item.name}
                </a>
              ))}
            </nav>
          </div>
        </div>
        <div className="flex flex-col h-fit w-full min-w-max">
          <div className="w-full">
            <div className="sticky flex top-0 z-10 h-16 flex-shrink-0 bg-white shadow">
              <div className={"flex mx-5 flex-1"}>
                <h2 className={"self-center text-xl font-semibold text-gray-900"}>{company.displayId}  {company?.name}</h2>
              </div>
              <button
                type="button"
                className="border-r border-gray-200 px-4 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
                onClick={() => {if(window.confirm('ログアウトしますか？')) auhtLogout()}}
              >
                <span className="sr-only">Open sidebar</span>
                ログアウト
              </button>
            </div>
            <Outlet />
          </div>
        </div>
      </div>
    </>
  );
};
